import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchAllPages } from '../components/shared/fetchAllPages';

/**
 * GA4 paginated fetch-all — fetches page 1 then remaining pages in parallel.
 *
 * Stale-while-revalidate: when args change, previous data stays visible during the
 * refetch. `loading` only flips true on first load (when there's no data yet).
 * Use `isRevalidating` if you want to show a subtle "refreshing" indicator.
 *
 * @param {Function} apiFn       – async (…args, params) => { data: { items, pagination } }
 * @param {Array}    args        – base arguments forwarded to apiFn (also used as deps)
 * @param {Object}   [opts]
 * @param {boolean}  [opts.skip]      – skip fetching when true
 * @param {number}   [opts.limit]     – page size (default 100)
 * @param {string}   [opts.sortBy]    – sort field (default 'sessions')
 * @param {string}   [opts.sortDir]   – sort direction (default 'desc')
 * @returns {{ data, loading, error, isRevalidating }}
 */
const useGA4PaginatedFetch = (apiFn, args = [], { skip = false, limit = 100, sortBy = 'sessions', sortDir = 'desc' } = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRevalidating, setIsRevalidating] = useState(false);
  const dataRef = useRef(null);
  // Monotonic request id — see useGA4Fetch for the rationale. Especially important
  // here: this hook fans out into N parallel page requests, so a superseded run
  // has many chances to resolve after the newer one.
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    if (skip) return;
    const myReq = ++reqIdRef.current;
    if (dataRef.current == null) setLoading(true);
    setIsRevalidating(true);
    try {
      const baseParams = { limit, sort_by: sortBy, sort_dir: sortDir };
      // Shared paging loop (fetch page 1 → parallel-fetch the rest → concat items).
      // firstPageBareFallback preserves this hook's original tolerance for a `data`
      // that is itself a bare array rather than a { items } envelope.
      const allItems = await fetchAllPages(
        async (page) => (await apiFn(...args, { ...baseParams, page }))?.data ?? {},
        { firstPageBareFallback: true }
      );
      if (myReq !== reqIdRef.current) return;
      dataRef.current = allItems;
      setData(allItems);
      setError('');
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      // Keep stale data on error rather than wiping it; only surface an error
      // (for the consumer's error branch) when there's nothing to show.
      if (dataRef.current == null) {
        setData(null);
        setError(e?.message || 'Failed to load data');
      }
    } finally {
      // Only the newest request may clear the busy flags (see useGA4Fetch).
      if (myReq === reqIdRef.current) {
        setLoading(false);
        setIsRevalidating(false);
      }
    }
  }, [skip, limit, sortBy, sortDir, ...args]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, isRevalidating };
};

export default useGA4PaginatedFetch;
